import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { type Control, useForm } from 'react-hook-form'

import { Alert, Button, Checkbox } from '@atoms'
import Badge from '@components/atoms/Badge'
import { PageLayout } from '@components/layouts/PageLayout'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { SelectField } from '@components/molecules/SelectField'
import Steps from '@components/molecules/Steps'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

import { DesignExplorer, SegControl } from '../components/DesignExplorer'
import { ImportSummary } from '../components/imports/ImportSummary'
import {
  ALL_GROUPS,
  COLUMN_BUNDLES,
  type ColumnSpec,
  GROUP,
  EVENTS,
  type ImportRun,
  type ImportType,
  MAX_ROWS,
  RSVP_ONLY_COLUMNS,
  SETTLED_RUN,
  SUPPORTER_COLUMNS,
  WORKING_RUN,
} from '../components/imports/sampleData'
import { readStepParam, writeStepParam } from '../components/imports/stepParam'
import { AlertText, Card, CardHeading } from '../components/imports/ui'

/**
 * Single-group supporter import (design-phase prototype, story 02 / P0.1)
 *
 * Every import goes into one group, stored on the run and never a column in
 * the file. A captain starts from the group page (`/groups/:id/imports/new`,
 * P3.1), where the URL fixes the group. An admin starts from `/imports/new`
 * (P0.2) and picks the group on step 1. Three steps, each deep-linkable with
 * `?step=template|upload|confirm`: build the template, upload through CSVBox,
 * then the import summary (shared with ImportSummaryPreview).
 *
 * Sample data only. Nothing here talks to a controller, GraphQL or CSVBox.
 */

type Entry = 'group' | 'imports'

type SummaryState = 'working' | 'settled'

const STEP_LABELS = ['Build template', 'Upload file', 'Confirm']

const ROOT_ID = 'supporter-import-preview'

const templateFileName = (type: ImportType, group: string | null) => {
  const suffix = type === 'supporters' ? 'supporters' : 'rsvps'
  return group ? `${group.toLowerCase().replaceAll(' ', '-')}-${suffix}.csv` : `${suffix}.csv`
}

const groupOrFallback = (group: string | null, type: ImportType = 'supporters') =>
  group ?? (type === 'supporters' ? 'the group you choose' : "the event's group")

const REQUIRED_GROUP = { group: { type: 'required', message: 'Choose the group this import is for.' } }
const REQUIRED_EVENT = { event: { type: 'required', message: 'Choose the event these RSVPs are for.' } }

// ---------------------------------------------------------------------------
// Step 1: build the template
// ---------------------------------------------------------------------------

type ColumnsForm = { columns: Record<string, boolean> }

function ImportTypeCard({
  type,
  onTypeChange,
  eventId,
  onEventChange,
  available,
  columnsControl,
  group,
  entry,
  pickedGroup,
  onGroupChange,
  showErrors,
  groupFieldRef,
  eventFieldRef,
}: {
  type: ImportType
  onTypeChange: (type: ImportType) => void
  eventId: string | null
  onEventChange: (eventId: string | null) => void
  available: ColumnSpec[]
  columnsControl: Control<ColumnsForm>
  group: string | null
  entry: Entry
  pickedGroup: string | null
  onGroupChange: (group: string) => void
  showErrors: boolean
  groupFieldRef: RefObject<HTMLButtonElement | null>
  eventFieldRef: RefObject<HTMLButtonElement | null>
}) {
  const events = entry === 'group' ? EVENTS.filter((event) => event.group === GROUP.name) : EVENTS
  const fixed = available.filter((column) => column.alwaysIncluded)
  // A bundle (the address) is one checkbox, placed where its first column sits.
  const choices = available
    .filter((column) => !column.alwaysIncluded)
    .flatMap((column) => {
      if (!column.bundle)
        return [{ key: column.name, label: column.label, hint: column.hint || `For example, ${column.example}` }]
      const isFirst = available.find((candidate) => candidate.bundle === column.bundle) === column
      return isFirst ? [{ key: column.bundle, ...COLUMN_BUNDLES[column.bundle] }] : []
    })

  return (
    <Card>
      <CardHeading icon="file-05" title="What are you importing?" />
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        <RadioGroupField<ImportType>
          label="Import type"
          appearance="cards"
          value={type}
          onValueChange={onTypeChange}
          options={[
            {
              value: 'supporters',
              label: 'Supporters',
              description: `Add people to ${groupOrFallback(group)}, or update people already in it.`,
            },
            {
              value: 'event_rsvps',
              label: 'Event RSVPs',
              description:
                entry === 'group'
                  ? "Record who signed up for one of this group's events."
                  : 'Record who signed up for an event.',
            },
          ]}
        />
        {type === 'supporters' && entry === 'imports' && (
          <div className="tw:max-w-md">
            <SelectField
              search
              required
              ref={groupFieldRef}
              name="group"
              label="Group"
              description="Everyone in the file becomes a member of this group."
              placeholder="Choose a group"
              options={ALL_GROUPS.map((name) => ({ value: name, label: name }))}
              value={pickedGroup}
              onValueChange={onGroupChange}
              errors={showErrors && !pickedGroup ? REQUIRED_GROUP : undefined}
            />
          </div>
        )}
        {type === 'event_rsvps' && (
          <div className="tw:max-w-md">
            <SelectField
              search
              ref={eventFieldRef}
              name="event"
              label="Event"
              required
              description="Every row RSVPs to this one event."
              placeholder="Choose an event"
              options={events.map((event) => ({
                value: event.id,
                label: [event.name, entry === 'imports' && event.group, `${event.date}${event.past ? ' (past)' : ''}`]
                  .filter(Boolean)
                  .join(' · '),
              }))}
              value={eventId}
              onValueChange={onEventChange}
              errors={showErrors && !eventId ? REQUIRED_EVENT : undefined}
            />
          </div>
        )}
      </div>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <h3 className="tw:text-sm tw:font-medium tw:text-gray-cool-900">Columns in your template</h3>
          <p className="tw:text-sm tw:text-gray-cool-600">
            Every row needs an email address, and a first or last name, so those are always included. Add the others you
            have.
          </p>
        </div>
        <ul aria-label="Always included" className="tw:flex tw:flex-wrap tw:gap-1.5">
          {fixed.map((column) => (
            <li key={column.name}>
              <Badge color="muted" shape="rounded" leadingIcon="check">
                {column.label}
              </Badge>
            </li>
          ))}
        </ul>
        <fieldset className="tw:grid tw:gap-x-6 tw:gap-y-3 tw:sm:grid-cols-2">
          <legend className="tw:sr-only">Other columns</legend>
          {choices.map((choice) => (
            <div key={choice.key} className="tw:flex tw:flex-wrap tw:content-start tw:items-start">
              <Checkbox control={columnsControl} name={`columns.${choice.key}`} description={choice.hint}>
                {choice.label}
              </Checkbox>
            </div>
          ))}
        </fieldset>
      </div>
    </Card>
  )
}

function WhatHappensCard({ type, group }: { type: ImportType; group: string | null }) {
  const groupName = groupOrFallback(group)
  const items =
    type === 'supporters'
      ? [
          {
            title: 'Each row is one person, found by email',
            body: `Someone already in SupporterBase is updated. Someone new is created. Either way they are added to ${groupName}.`,
          },
          {
            title: 'What you provide is updated, what you leave blank is kept',
            body: 'A filled cell replaces the stored value. A blank cell leaves the stored value alone. The address is the exception: if your file has any part of an address, the whole stored address is replaced with what your file has, so the parts always agree.',
          },
          {
            title: 'Tags are added, never removed',
            body: `Tags in your file go on the person alongside the tags they already have. Everyone also gets the group's tag.`,
          },
        ]
      : [
          {
            title: 'Each row is one person signed up for the event',
            body: `They are found by email, or created if they are new, and signed up for the event you chose. They are not added to ${groupOrFallback(group, type)}.`,
          },
          {
            title: 'Someone already signed up is updated, not duplicated',
            body: 'Their guest count and attendance are set from your row.',
          },
          {
            title: 'Past events can be marked as attended',
            body: 'If the event has already happened, you can tick "Mark everyone as attended" on the next step.',
          },
        ]

  return (
    <Card>
      <CardHeading icon="info-circle" title="What this import does" />
      <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5">
        {items.map((item) => (
          <li key={item.title} className="tw:flex tw:items-start tw:gap-3 tw:py-3">
            <Icon type="check-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" aria-hidden />
            <div className="tw:flex tw:flex-col tw:gap-0.5">
              <span className="tw:text-sm tw:font-medium tw:text-gray-cool-900">{item.title}</span>
              <span className="tw:text-sm tw:text-gray-cool-600">{item.body}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
        <Alert type="warning" size="sm" className="tw:text-sm">
          <AlertText>
            Every row needs an email address. Rows without one, duplicate rows and rows we could not import are listed
            in the import summary, with the reason, so you can fix and upload only those.
          </AlertText>
        </Alert>
        <p className="tw:text-xs tw:text-gray-cool-600">
          Up to {MAX_ROWS.toLocaleString()} rows in one file. Split a bigger list into several files.
        </p>
      </div>
    </Card>
  )
}

function TemplateSummary({
  type,
  columns,
  hasDownloaded,
  onDownload,
  eventName,
  group,
  entry,
}: {
  type: ImportType
  columns: ColumnSpec[]
  hasDownloaded: boolean
  onDownload: () => void
  eventName: string | null
  group: string | null
  entry: Entry
}) {
  const fileName = templateFileName(type, group)

  return (
    <Card className="tw:lg:sticky tw:lg:top-4">
      <CardHeading icon="file-download-03" title="Your template" />
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        <dl className="tw:flex tw:flex-col tw:gap-2 tw:text-sm">
          <SummaryRow label="Group" value={group ?? 'Not chosen yet'} />
          <SummaryRow label="Import" value={type === 'supporters' ? 'Supporters' : 'Event RSVPs'} />
          {type === 'event_rsvps' && <SummaryRow label="Event" value={eventName ?? 'Not chosen yet'} />}
          <SummaryRow label="Columns" value={String(columns.length)} />
          <SummaryRow label="Row limit" value={MAX_ROWS.toLocaleString()} />
        </dl>

        <p className="tw:text-xs tw:text-gray-cool-600">
          {type === 'supporters'
            ? entry === 'group'
              ? `No group column. Every row goes into ${group} because that is where you started the import.`
              : 'No group column. Every row goes into the group you choose here, so one file is one group.'
            : 'No group or event column. Every row is signed up for the event you choose here.'}
        </p>

        <Button size="xl" rank="secondary" leadingIcon="file-download-03" onClick={onDownload} className="tw:w-full">
          {hasDownloaded ? 'Download again' : 'Download template'}
        </Button>

        <p
          className={cn(
            'tw:flex tw:items-center tw:gap-2 tw:text-xs',
            hasDownloaded ? 'tw:text-gray-cool-900' : 'tw:text-gray-cool-600'
          )}
          role="status"
        >
          {hasDownloaded && <Icon type="check-circle" className="tw:h-4 tw:w-4 tw:text-success-600" aria-hidden />}
          <span className="tw:font-mono">{fileName}</span>
        </p>
      </div>
    </Card>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-4">
      <dt className="tw:text-gray-cool-600">{label}</dt>
      <dd className="tw:text-right tw:font-medium tw:text-gray-cool-900">{value}</dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 2: upload
// ---------------------------------------------------------------------------

type OptionsForm = { markAsAttended: boolean }

/** Only RSVP imports have an option to set. There is no overwrite control: the import merges field by field (#8371). */
function OptionsCard({
  control,
  event,
}: {
  control: ReturnType<typeof useForm<OptionsForm>>['control']
  event: { name: string; past: boolean } | null
}) {
  const showAttended = event?.past

  return (
    <Card>
      <CardHeading icon="settings-01" title="Options" subtitle="Set these before you choose the file." />
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        <dl className="tw:flex tw:items-baseline tw:gap-3 tw:text-sm">
          <dt className="tw:text-gray-cool-600">Event</dt>
          <dd className="tw:font-medium tw:text-gray-cool-900">{event?.name ?? 'Not chosen yet'}</dd>
        </dl>

        {showAttended && (
          <div className="tw:flex tw:flex-wrap tw:items-start">
            <Checkbox
              control={control}
              name="markAsAttended"
              description="Only offered because this event has already happened."
            >
              Mark everyone in the file as attended
            </Checkbox>
          </div>
        )}
      </div>
    </Card>
  )
}

function UploadCard({
  type,
  group,
  eventName,
  onFileChosen,
}: {
  type: ImportType
  group: string | null
  eventName: string | null
  onFileChosen: () => void
}) {
  return (
    <Card>
      <CardHeading
        icon="upload-cloud-02"
        title="Upload your file"
        subtitle="CSV or Excel. Problems are highlighted on screen so you can fix them before you submit."
      />
      <div className="tw:p-5">
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-xl tw:border-2 tw:border-dashed tw:border-gray-cool-200 tw:bg-gray-cool-50/50 tw:px-6 tw:py-10 tw:text-center">
          <span
            aria-hidden
            className="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:text-primary-600 tw:shadow-xs"
          >
            <Icon type="upload-cloud-02" className="tw:h-6 tw:w-6" />
          </span>
          <p className="tw:text-sm tw:font-medium tw:text-gray-cool-900">Choose the file you filled in</p>
          <p className="tw:max-w-md tw:text-xs tw:text-gray-cool-600">
            Matched to the {type === 'supporters' ? 'supporter' : 'RSVP'} columns, up to {MAX_ROWS.toLocaleString()}{' '}
            rows.{' '}
            {type === 'supporters'
              ? `Every row goes into ${groupOrFallback(group)}.`
              : `Every row is signed up for ${eventName ?? 'the event you chose'}.`}
          </p>
          <Button size="xl" leadingIcon="plus-circle" onClick={onFileChosen}>
            Choose file
          </Button>
        </div>
      </div>
    </Card>
  )
}

const BEFORE_SUBMIT = [
  'Your column names are matched to the template, even if you renamed or moved them.',
  'Every row needs an email address, and a first or last name.',
  'Dates must be YYYY-MM-DD. Phone numbers must be digits.',
  `Files with more than ${MAX_ROWS.toLocaleString()} rows are stopped here.`,
]

const AFTER_SUBMIT = [
  'SupporterBase works through the rows one at a time, in the background.',
  'The import summary on the next step fills in as it goes, and we email you when it finishes.',
  'Rows that could not be imported are listed in the summary with the reason.',
]

function ChecksCard({ title, subtitle, items }: { title: string; subtitle: string; items: string[] }) {
  return (
    <Card>
      <CardHeading icon="check-done-01" title={title} subtitle={subtitle} />
      <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5 tw:text-sm">
        {items.map((line) => (
          <li key={line} className="tw:flex tw:items-start tw:gap-2.5 tw:py-3 tw:text-gray-cool-700">
            <Icon type="check-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" aria-hidden />
            {line}
          </li>
        ))}
      </ul>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** Adapt the shared sample run to what was chosen on steps 1 and 2. */
function runFor(
  state: SummaryState,
  type: ImportType,
  group: string | null,
  event: { name: string } | null,
  options: OptionsForm
): ImportRun {
  const base = state === 'working' ? WORKING_RUN : SETTLED_RUN
  return {
    ...base,
    type,
    group: group ?? base.group,
    fileName: templateFileName(type, group),
    event: type === 'event_rsvps' ? (event?.name ?? null) : null,
    overwrite: false,
    markAsAttended: type === 'event_rsvps' && options.markAsAttended,
  }
}

const SupporterImportPreview = () => {
  const [step, setStep] = useState(readStepParam)
  const [type, setType] = useState<ImportType>('supporters')
  const [eventId, setEventId] = useState<string | null>(null)
  const [hasDownloaded, setHasDownloaded] = useState(false)

  const [entry, setEntry] = useState<Entry>('imports')
  const [pickedGroup, setPickedGroup] = useState<string | null>(null)
  const [triedToContinue, setTriedToContinue] = useState(false)

  const [summaryState, setSummaryState] = useState<SummaryState>('working')

  const { control, watch } = useForm<OptionsForm>({ defaultValues: { markAsAttended: false } })
  const options = watch()

  const { control: columnsControl, watch: watchColumns } = useForm<ColumnsForm>({ defaultValues: { columns: {} } })
  const picked = watchColumns('columns')

  // A different set of columns is a different template, so it needs downloading again.
  useEffect(() => {
    const subscription = watchColumns(() => setHasDownloaded(false))
    return () => subscription.unsubscribe()
  }, [watchColumns])

  const event = useMemo(() => EVENTS.find((candidate) => candidate.id === eventId) ?? null, [eventId])
  // An RSVP import is recorded against the event's group, never one chosen separately.
  const group = entry === 'group' ? GROUP.name : type === 'event_rsvps' ? (event?.group ?? null) : pickedGroup
  const available = type === 'supporters' ? SUPPORTER_COLUMNS : [...SUPPORTER_COLUMNS, ...RSVP_ONLY_COLUMNS]
  const columns = available.filter((column) => column.alwaysIncluded || picked?.[column.bundle ?? column.name])

  // Each step is a new screen, so it starts at the top. PageLayout.Root is the
  // scrolling element here, not the window.
  const goToStep = (next: number) => {
    setStep(next)
    document.getElementById(ROOT_ID)?.scrollTo?.({ top: 0 })
    window.scrollTo({ top: 0 })
    writeStepParam(next)
  }

  const changeType = (next: ImportType) => {
    setType(next)
    setHasDownloaded(false)
  }

  const changeGroup = (next: string) => {
    setPickedGroup(next)
    setHasDownloaded(false)
  }

  const changeEvent = (next: string | null) => {
    setEventId(next)
    setHasDownloaded(false)
  }

  const groupFieldRef = useRef<HTMLButtonElement>(null)
  const eventFieldRef = useRef<HTMLButtonElement>(null)

  const missingChoice = type === 'event_rsvps' ? !eventId : !group

  // Focusing the field scrolls it into view and lands keyboard and screen-reader users on its error.
  const continueToUpload = () => {
    if (missingChoice) {
      setTriedToContinue(true)
      const field = type === 'event_rsvps' ? eventFieldRef.current : groupFieldRef.current
      field?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
      field?.focus({ preventScroll: true })
      return
    }
    setTriedToContinue(false)
    goToStep(1)
  }

  // Submitting the file is what starts the run, so landing on step 3 means the run is working.
  const submitFile = () => {
    setSummaryState('working')
    goToStep(2)
  }

  // What Continue commits to, or the one thing still missing before it can.
  const footerSummary =
    type === 'supporters'
      ? group
        ? `Supporters into ${group}`
        : 'Choose a group to continue'
      : event
        ? `RSVPs for ${event.name}${entry === 'imports' ? `, ${event.group}` : ''}`
        : 'Choose an event to continue'

  const run = runFor(summaryState, type, group, event, options)

  return (
    <PageLayout.Root id={ROOT_ID}>
      <PageLayout.Header
        icon="users-01"
        heading={type === 'supporters' ? 'Import supporters' : 'Import event RSVPs'}
        subtitle={
          entry === 'group' ? (
            <>
              {type === 'supporters' ? 'Into' : 'For'}{' '}
              <span className="tw:font-medium tw:text-gray-cool-900">{GROUP.name}</span> ·{' '}
              {GROUP.supporterCount.toLocaleString()} supporters
            </>
          ) : group ? (
            <>
              {type === 'supporters' ? 'Into' : 'For'}{' '}
              <span className="tw:font-medium tw:text-gray-cool-900">{group}</span>
            </>
          ) : type === 'supporters' ? (
            'Add or update supporters from a CSV or Excel file.'
          ) : (
            'Record who signed up for an event, from a CSV or Excel file.'
          )
        }
      />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-col tw:gap-6">
          <div className="tw:py-2">
            <Steps steps={STEP_LABELS} currentStep={step} />
          </div>

          {step === 0 && (
            <div className="tw:grid tw:gap-6 tw:lg:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="tw:flex tw:flex-col tw:gap-5">
                <ImportTypeCard
                  type={type}
                  onTypeChange={changeType}
                  eventId={eventId}
                  onEventChange={changeEvent}
                  available={available}
                  columnsControl={columnsControl}
                  group={group}
                  entry={entry}
                  pickedGroup={pickedGroup}
                  onGroupChange={changeGroup}
                  showErrors={triedToContinue}
                  groupFieldRef={groupFieldRef}
                  eventFieldRef={eventFieldRef}
                />
                <WhatHappensCard type={type} group={group} />
              </div>
              <TemplateSummary
                type={type}
                columns={columns}
                hasDownloaded={hasDownloaded}
                onDownload={() => setHasDownloaded(true)}
                eventName={event?.name ?? null}
                group={group}
                entry={entry}
              />
            </div>
          )}

          {step === 1 && (
            <div className="tw:flex tw:flex-col tw:gap-5">
              {type === 'event_rsvps' && <OptionsCard control={control} event={event} />}
              <UploadCard type={type} group={group} eventName={event?.name ?? null} onFileChosen={submitFile} />
              <div className="tw:grid tw:gap-5 tw:md:grid-cols-2">
                <ChecksCard
                  title="Before you submit"
                  subtitle="Problem cells are highlighted. Fix them on screen."
                  items={BEFORE_SUBMIT}
                />
                <ChecksCard title="After you submit" subtitle="What happens to the rows." items={AFTER_SUBMIT} />
              </div>
            </div>
          )}

          {step === 2 && <ImportSummary run={run} viewer={entry === 'group' ? 'captain' : 'admin'} embedded />}
        </div>
      </PageLayout.Body>

      {step < 2 && (
        <PageLayout.Footer>
          <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-wrap tw:items-center tw:gap-3">
            {step === 0 && (
              <>
                <p
                  className={cn(
                    'tw:mb-0 tw:mr-auto tw:flex tw:items-center tw:gap-1.5 tw:text-sm',
                    triedToContinue && missingChoice ? 'tw:text-error-600' : 'tw:text-gray-cool-600'
                  )}
                >
                  {triedToContinue && missingChoice && (
                    <Icon type="alert-circle" className="tw:h-4 tw:w-4 tw:shrink-0" aria-hidden />
                  )}
                  {footerSummary}
                </p>
                <div className="tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                  <Button size="xl" trailingIcon="arrow-narrow-right" onClick={continueToUpload}>
                    Continue
                  </Button>
                </div>
              </>
            )}

            {step === 1 && (
              <div className="tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                <Button rank="secondary" onClick={() => goToStep(0)}>
                  Back to the template
                </Button>
              </div>
            )}
          </div>
        </PageLayout.Footer>
      )}

      <DesignExplorer>
        <SegControl
          label="Started from"
          value={entry}
          onChange={(value) => {
            setEntry(value)
            setEventId(null)
          }}
          options={[
            { value: 'group', label: 'Group page (captain)' },
            { value: 'imports', label: 'Imports page (admin)' },
          ]}
        />
        <SegControl
          label="Step"
          value={String(step)}
          onChange={(value) => goToStep(Number(value))}
          options={[
            { value: '0', label: 'Template' },
            { value: '1', label: 'Upload' },
            { value: '2', label: 'Confirm' },
          ]}
        />
        <SegControl
          label="Summary state"
          value={summaryState}
          onChange={(value) => setSummaryState(value)}
          options={[
            { value: 'working', label: 'Working' },
            { value: 'settled', label: 'Settled' },
          ]}
        />
      </DesignExplorer>
    </PageLayout.Root>
  )
}

export default SupporterImportPreview
